import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import { 
  normalizeProfileTrialData, 
  getTrialStatus, 
  getTrialDaysRemaining,
  isTrialExpired 
} from '@/lib/trialUtils';

interface TrialStatus {
  accountStatus: 'trial' | 'active' | 'expired' | 'cancelled' | null;
  trialStartDate: string | null;
  trialEndDate: string | null;
  trialConfirmed: boolean;
  firstAppEntryShown: boolean;
  daysRemaining: number;
  isExpired: boolean;
  loading: boolean;
}

export function useTrialStatus() {
  const { user } = useAuth();
  const [status, setStatus] = useState<TrialStatus>({
    accountStatus: null,
    trialStartDate: null,
    trialEndDate: null,
    trialConfirmed: false,
    firstAppEntryShown: false,
    daysRemaining: 0,
    isExpired: false,
    loading: true,
  });

  const fetchTrialStatus = useCallback(async () => {
    if (!user) {
      setStatus(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      // First, try to select only guaranteed columns + trial_welcome_seen_at + is_internal
      const { data, error } = await supabase
        .from('profiles')
        .select('account_status, trial_start_date, trial_end_date, trial_welcome_seen_at, is_internal')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('[useTrialStatus] Error fetching trial status:', {
          code: error.code,
          message: error.message,
          details: error
        });
        // Use safe defaults on error
        const defaults = normalizeProfileTrialData(null);
        setStatus({
          accountStatus: defaults.account_status as TrialStatus['accountStatus'],
          trialStartDate: defaults.trial_start_date,
          trialEndDate: defaults.trial_end_date,
          trialConfirmed: false,
          firstAppEntryShown: false,
          daysRemaining: 0,
          isExpired: false,
          loading: false,
        });
        return;
      }

      // Check if user is internal (bypasses all trial restrictions)
      const isInternal = (data as any)?.is_internal === true;

      // Normalize data with safe defaults
      const normalized = normalizeProfileTrialData(data);
      const trialStatus = getTrialStatus(data);
      const daysRemaining = getTrialDaysRemaining(data);
      const expired = isTrialExpired(data);

      // Internal users: force active status and bypass trial restrictions
      if (isInternal) {
        setStatus({
          accountStatus: 'active',
          trialStartDate: null,
          trialEndDate: null,
          trialConfirmed: true,
          firstAppEntryShown: true,
          daysRemaining: 999, // Show as unlimited
          isExpired: false,
          loading: false,
        });
        return;
      }

      // Optional columns may not exist in schema - use safe defaults
      // Don't query for them separately to avoid 400 errors
      let trialConfirmed = false;
      let firstAppEntryShown = false;
      
      // If data exists, try to read optional fields (may be null/undefined if columns don't exist)
      if (data) {
        // Safely access optional fields without causing errors
        trialConfirmed = (data as any).trial_confirmed ?? false;
        // Check trial_welcome_seen_at (preferred) or fallback to first_app_entry_shown
        const trialWelcomeSeenAt = (data as any).trial_welcome_seen_at;
        const firstAppEntryShownLegacy = (data as any).first_app_entry_shown ?? false;
        // If trial_welcome_seen_at exists and is not null, modal was shown
        firstAppEntryShown = trialWelcomeSeenAt != null || firstAppEntryShownLegacy;
      }
      
      // Check localStorage fallback
      if (!firstAppEntryShown && user?.id) {
        const localStorageKey = `trial_welcome_seen:${user.id}`;
        const seenInStorage = localStorage.getItem(localStorageKey);
        if (seenInStorage === 'true') {
          firstAppEntryShown = true;
        }
      }

      setStatus({
        accountStatus: normalized.account_status as TrialStatus['accountStatus'],
        trialStartDate: normalized.trial_start_date,
        trialEndDate: normalized.trial_end_date,
        trialConfirmed,
        firstAppEntryShown,
        daysRemaining,
        isExpired: expired,
        loading: false,
      });
    } catch (error) {
      console.error('[useTrialStatus] Unexpected error fetching trial status:', error);
      // Use safe defaults on unexpected error
      const defaults = normalizeProfileTrialData(null);
      setStatus({
        accountStatus: defaults.account_status as TrialStatus['accountStatus'],
        trialStartDate: defaults.trial_start_date,
        trialEndDate: defaults.trial_end_date,
        trialConfirmed: false,
        firstAppEntryShown: false,
        daysRemaining: 0,
        isExpired: false,
        loading: false,
      });
    }
  }, [user]);

  useEffect(() => {
    fetchTrialStatus();
  }, [fetchTrialStatus]);

  const markFirstEntryShown = async () => {
    if (!user) return;

    try {
      // Update trial_welcome_seen_at (preferred) or fallback to first_app_entry_shown
      const updateData: any = { trial_welcome_seen_at: new Date().toISOString() };
      
      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id);

      if (error) {
        // If trial_welcome_seen_at column doesn't exist, try first_app_entry_shown
        if (error.code === '42883' || error.message?.includes('column') || error.message?.includes('does not exist')) {
          console.warn('[useTrialStatus] trial_welcome_seen_at column not available, trying first_app_entry_shown');
          const { error: fallbackError } = await supabase
            .from('profiles')
            .update({ first_app_entry_shown: true })
            .eq('id', user.id);
          
          if (fallbackError) {
            console.warn('[useTrialStatus] Both columns not available, using localStorage only');
          }
        } else {
          console.error('[useTrialStatus] Error marking first entry shown:', {
            code: error.code,
            message: error.message,
            details: error
          });
        }
      }

      // Always update localStorage as fallback
      const localStorageKey = `trial_welcome_seen:${user.id}`;
      localStorage.setItem(localStorageKey, 'true');

      setStatus(prev => ({ ...prev, firstAppEntryShown: true }));
    } catch (error) {
      console.error('[useTrialStatus] Unexpected error marking first entry shown:', error);
      // Don't throw - this is non-critical
      // Still update localStorage
      if (user?.id) {
        const localStorageKey = `trial_welcome_seen:${user.id}`;
        localStorage.setItem(localStorageKey, 'true');
        setStatus(prev => ({ ...prev, firstAppEntryShown: true }));
      }
    }
  };

  return {
    ...status,
    refetch: fetchTrialStatus,
    markFirstEntryShown,
  };
}
